import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { setUserContext, clearUserContext } from '../sentry';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'client' | 'pt';
  pt_id?: string;
  profileCompleted?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, role: 'client' | 'pt', pt_id?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        try {
          // Check auth status from server
          const response = await fetch('/api/auth/check', {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          
          if (response.ok) {
            const data = await response.json();
            setToken(storedToken);
            setUser(data.user);
          } else {
            // Clear invalid token
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email: string, password: string = '') => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(errorData.error || errorData.error?.message || 'Login failed');
    }

    const data = await response.json();

    // Handle both old and new response formats
    const token = data.token || data.data?.token;
    const user = data.user || data.data?.user;

    if (!token || !user) {
      throw new Error('Invalid response from server');
    }

    setToken(token);
    setUser(user);

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    // Set user context in Sentry
    setUserContext(user);
  };

  const register = async (email: string, password: string = '', name: string, role: 'client' | 'pt', pt_id?: string) => {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, role, pt_id, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Registration failed' }));
      throw new Error(errorData.error || errorData.error?.message || 'Registration failed');
    }

    const data = await response.json();
    console.log('[AuthContext] Full registration response:', JSON.stringify(data, null, 2));

    // Check if API returned an error
    if (data.success === false && data.error) {
      console.error('[AuthContext] API returned error:', data.error);
      throw new Error(data.error.message || data.error.details || 'Registration failed');
    }

    // Handle both old and new response formats
    let token, user;

    if (data.success && data.data) {
      // New format: {success: true, data: {token, user}}
      token = data.data.token;
      user = data.data.user;
    } else if (data.token && data.user) {
      // Old format: {token, user}
      token = data.token;
      user = data.user;
    }

    console.log('[AuthContext] Extracted - token:', !!token, 'user:', !!user);

    if (!token || !user) {
      console.error('[AuthContext] Failed to extract. Data structure:', data);
      throw new Error('Invalid response from server. Please check console and try again.');
    }

    setToken(token);
    setUser(user);

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));

    // Set user context in Sentry
    setUserContext(user);
  };

  const refreshUser = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/auth/check', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else if (response.status === 401) {
        // Token is invalid or expired - silently clear auth state
        logout();
      } else {
        // Unexpected error status
        console.error('Unexpected auth check response:', response.status);
      }
    } catch (error) {
      // Network error or other unexpected issue
      console.error('Error refreshing user:', error);
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear user context in Sentry
    clearUserContext();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}